import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import FormHeader from 'components/shared/form/FormHeader';
import { MultiSelectChips, Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { ATTR_OLIO_MAPPED_FIELDS } from 'constants/attrMappedFields';
import { useProfile } from 'context/profile';
import { getFieldError, useAdminForm } from 'hooks/datasource';
import { useDebounce } from 'hooks/useDebounce';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { Attr } from 'models/flexible/Attr';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminIndexAttrs, fetchAttrMatches } from 'services/api/admin/attrs';
import {
  adminAttrValuesQueryKeys,
  adminShowAttrValue,
  createAttrValue,
  updateAttrValue,
} from 'services/api/admin/attrValues';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminIndexGroups } from 'services/api/admin/groups';
import type { AdminAttrIndexFields } from 'services/api/fields';

const schema = yup.object({
  name: yup.string().required('Raw value is required'),
  client: yup.mixed().test('client', 'Client is required', (v: any) => !!v?.id),
  attr: yup.mixed().test('attr', 'Attribute is required', (v: any) => !!v?.id),
  displayName: yup.string().required('Display value is required'),
});

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;

export default function EditAttributeValue() {
  const { id } = useParams();
  const isEditing = !!id;
  const { profile } = useProfile();

  const canEdit = profile.has(PERMISSIONS.adminAttrValueEdit);

  const form = useAdminForm({
    id,
    show: adminShowAttrValue,
    create: createAttrValue,
    update: (updateId, payload) => updateAttrValue({ id: updateId, ...payload }),
    include: ['client', 'attr', 'associatedGroups'] as const,
    queryKey: adminAttrValuesQueryKeys,
    schema,
    toFormValues: (data) => ({
      name: data?.name ?? '',
      displayName: data?.displayName ?? '',
      client: data?.client ?? null,
      attr: data?.attr ?? null,
      visible: data?.visible ?? false,
      active: data?.active ?? true,
      associatedGroups: data?.associatedGroups ?? [],
    }),
    toPayload: (v) => ({
      attrId: v.attr?.id,
      clientId: v.client?.id || '',
      name: v.name ?? '',
      displayName: v.displayName || null,
      visible: v.visible ?? false,
      active: v.active ?? true,
      associatedGroupIds: (v.associatedGroups ?? []).map((g) => g.id),
    }),
    onCreateSuccess: { toast: 'Value successfully added!', navigate: '/admin/attributes/values' },
    onUpdateSuccess: { toast: 'Value successfully updated!', navigate: '/admin/attributes/values' },
  });

  const clientId = form.values.client?.id;
  const attr = form.values.attr;
  const hasOlioAttr = !!attr?.olioAttr;

  const clientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'attrValues'],
    fetchFn: adminIndexClients,
    params: { sortBy: 'name asc' },
  });

  const attrOpts = useInfiniteOptions({
    queryKey: ['adminAttrs', clientId],
    fetchFn: adminIndexAttrs,
    params: { client: clientId, sortBy: 'name asc' },
  });

  const olioAttrApiName = ATTR_OLIO_MAPPED_FIELDS.find(({ value }) => value === attr?.olioAttr)?.apiName;

  const groupOpts = useInfiniteOptions({
    queryKey: ['adminGroups', 'attrValues', clientId, olioAttrApiName],
    fetchFn: adminIndexGroups,
    params: { clientId, type: olioAttrApiName, sortBy: 'name asc' },
  });

  const olioAttrObjectType = attr?.olioAttrObjectType;
  const matchesSearch = form.values.displayName || form.values.name;
  const debouncedMatchesSearch = useDebounce(matchesSearch, 250);
  const [matches, setMatches] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    if (attr?.olioAttr && debouncedMatchesSearch && attr.id) {
      fetchAttrMatches(attr.id, debouncedMatchesSearch).then((data) => setMatches(data));
    } else {
      setMatches([]);
    }
  }, [attr?.id, attr?.olioAttr, debouncedMatchesSearch]);

  const handleClientChange = (v: { id: string; name: string } | null) => {
    form.setValue('client', v);
    form.setValue('attr', null);
    form.setValue('associatedGroups', []);
  };

  const handleAttrChange = (v: Attr<AdminAttrIndexFields> | null) => {
    form.setValue('attr', v);
    form.setValue('visible', !!v?.olioAttr);
    form.setValue('associatedGroups', []);
  };

  if (isEditing && form.loading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <FormHeader title={`${isEditing ? 'Edit' : 'Add'} Value`} />

      <InputGroup title='Client' error={getFieldError(form.errors, 'client')}>
        <Select
          {...clientOpts}
          value={form.values.client}
          onChange={handleClientChange}
          getOptionValue={getId}
          getOptionLabel={getName}
          placeholder='Client'
          isDisabled={isEditing}
        />
      </InputGroup>

      <SectionHeader>Attribute Details</SectionHeader>

      <InputGroup title='Raw Attribute' error={getFieldError(form.errors, 'attr')}>
        <Select
          {...attrOpts}
          value={form.values.attr}
          onChange={handleAttrChange}
          getOptionValue={getId}
          getOptionLabel={getName}
          placeholder='Raw Attribute'
          isDisabled={isEditing || !clientId}
        />
      </InputGroup>

      <InputGroup title='Mapped Olio Field'>
        <Input value={attr?.olioAttr ? (attr?.displayName ?? '') : ''} disabled />
      </InputGroup>

      <InputGroup title='Display Attribute'>
        <Input value={attr?.displayName ?? ''} disabled />
      </InputGroup>

      <SectionHeader>Value Details</SectionHeader>

      <InputGroup title='Raw Value' error={form.errors.name}>
        <Input
          value={form.values.name ?? ''}
          onChange={(e) => form.setValue('name', e.target.value)}
          $hasError={!!form.errors.name}
          disabled={isEditing}
          onBlur={() => {
            if (form.values.name && !form.values.displayName) form.setValue('displayName', form.values.name);
          }}
        />
      </InputGroup>

      <CheckboxRow>
        <input
          type='checkbox'
          checked={form.values.visible ?? false}
          onChange={(e) => form.setValue('visible', e.target.checked)}
          disabled={hasOlioAttr || !canEdit}
        />
        <span>Display on UI</span>
      </CheckboxRow>

      <InputGroup title='Display Value' error={form.errors.displayName}>
        <Input
          value={form.values.displayName ?? ''}
          onChange={(e) => form.setValue('displayName', e.target.value)}
          $hasError={!!form.errors.displayName}
          disabled={!canEdit}
        />
      </InputGroup>

      {hasOlioAttr && olioAttrObjectType && (
        <>
          <InputGroup>
            <MultiSelectChips
              title={`Matched ${olioAttrObjectType}s`}
              options={[]}
              values={matches}
              onChange={() => {}}
              getOptionValue={(o) => o.id}
              getOptionLabel={(o) => o.name}
              placeholder={`Matched ${olioAttrObjectType}s`}
              disabled
            />
          </InputGroup>

          <InputGroup>
            <MultiSelectChips
              title={`Additional Associated ${olioAttrObjectType}s (optional)`}
              {...groupOpts}
              values={form.values.associatedGroups}
              onChange={(v) => form.setValue('associatedGroups', v)}
              getOptionValue={(o) => o.id}
              getOptionLabel={(o) => o.name}
              placeholder={`Additional Associated ${olioAttrObjectType}s`}
              disabled={!canEdit}
            />
          </InputGroup>
        </>
      )}

      <ButtonGroup style={{ justifyContent: 'flex-end' }}>
        <Button variant='ghost' onClick={form.goBack}>
          Cancel
        </Button>
        <Button onClick={form.submit} disabled={!form.valid || form.submitting || !canEdit} loading={form.submitting}>
          Submit
        </Button>
      </ButtonGroup>
    </AdminFormPage>
  );
}

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
  }
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;
